import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';

import BomUnitsAnalysisInProgress from '../BomUnitsAnalysisInProgress/BomUnitsAnalysisInProgress';
import RowAICreatorBoM from '../RowAICreatorBoM/RowAICreatorBoM';

import type IBomUnitsTable from './interfaces/IBomUnitsTable';

import styles from './scss/BomUnitsTable.module.scss';

function BomUnitsTable({
  open,
  isAnalysisInProgress,
  isSelectAllLoading,
  isSelectAllChecked,
  onSelectAllChange,
  tableItems,
  naceOptions,
  categoryOptions,
  onUpdate,
  onDelete,
  onLoadMore,
  hasMore,
  emptyStateMessage,
  focusCell,
  onStartEdit,
}: IBomUnitsTable) {
  const { t } = useTranslation();
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [tableScrollbarWidth, setTableScrollbarWidth] = useState(0);
  const [tableWrapperWidth, setTableWrapperWidth] = useState(0);

  const tableHeaders = useMemo(() => ([
    {
      id: 'inputProduct',
      title: t('valueChainMap.bomUnitsInputProduct'),
      width: 220,
      hidden: false,
    },
    {
      id: 'code',
      title: t('valueChainMap.bomUnitsCode'),
      width: 140,
      hidden: false,
    },
    {
      id: 'description',
      title: t('valueChainMap.bomUnitsDescription'),
      width: 200,
      hidden: false,
    },
    {
      id: 'category',
      title: t('valueChainMap.bomUnitsCategory'),
      width: 130,
      hidden: false,
    },
    {
      id: 'suppliers',
      title: t('valueChainMap.bomUnitsSuppliers'),
      width: 200,
      hidden: false,
    },
    {
      id: 'naceSector',
      title: t('valueChainMap.bomUnitsNaceSector'),
      width: 130,
      hidden: false,
    },
    {
      id: 'additionalInformation',
      title: t('valueChainMap.bomUnitsAdditionalInformation'),
      width: 170,
      hidden: false,
    },
    {
      id: 'source',
      title: t('valueChainMap.bomUnitsSource'),
      width: 120,
      hidden: false,
    },
  ]), [t]);

  const visibleTableHeaders = useMemo(
    () => tableHeaders.filter((header) => !header.hidden),
    [tableHeaders],
  );

  const visibleColumnsCount = 1 + visibleTableHeaders.length + 1;
  const tableBaseWidth = useMemo(
    () => visibleTableHeaders.reduce((sum, header) => sum + header.width, 48 + 64),
    [visibleTableHeaders],
  );
  const tableMinScale = 0.85;
  const tableScale = useMemo(() => {
    if (!tableWrapperWidth) {
      return 1;
    }

    return Math.max(Math.min(tableWrapperWidth / tableBaseWidth, 1), tableMinScale);
  }, [tableBaseWidth, tableWrapperWidth]);
  const tableWidth = Math.round(tableBaseWidth * tableScale);

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;

    if (!tableWrapper) {
      return undefined;
    }

    const syncTableWrapperWidth = () => {
      setTableWrapperWidth(tableWrapper.clientWidth);
    };

    syncTableWrapperWidth();

    const resizeObserver = new ResizeObserver(() => {
      syncTableWrapperWidth();
    });

    resizeObserver.observe(tableWrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const syncTableScrollbarWidth = () => {
      const tableScrollContainer = tableScrollContainerRef.current;

      if (!tableScrollContainer) {
        setTableScrollbarWidth(0);
        return;
      }

      const nextScrollbarWidth = tableScrollContainer.offsetWidth - tableScrollContainer.clientWidth;
      setTableScrollbarWidth(nextScrollbarWidth);
    };

    syncTableScrollbarWidth();
    window.addEventListener('resize', syncTableScrollbarWidth);

    return () => {
      window.removeEventListener('resize', syncTableScrollbarWidth);
    };
  }, [open, tableItems.length, isAnalysisInProgress]);

  const renderTableColGroup = () => (
    <colgroup>
      <col style={{ width: `${Math.round(48 * tableScale)}px` }} />
      {visibleTableHeaders.map((column) => (
        <col key={column.id} style={{ width: `${Math.round(column.width * tableScale)}px` }} />
      ))}
      <col style={{ width: `${Math.round(64 * tableScale)}px` }} />
    </colgroup>
  );

  return (
    <div
      ref={tableWrapperRef}
      className={styles.tableWrapper}
      style={{ '--bom-units-table-width': `${tableWidth}px` } as React.CSSProperties}
    >
      <div className={styles.tableHeaderContainer} style={{ paddingRight: `${tableScrollbarWidth}px` }}>
        <Table className={styles.table}>
          {renderTableColGroup()}
          <TableHead className={styles.tableHead}>
            <TableCell head className={clsx(styles.tableCellHead, styles.checkboxHeadCell)}>
              <Checkbox
                name="bomUnitsSelectAll"
                value="all"
                checked={isSelectAllChecked}
                onChange={onSelectAllChange}
                disabled={isAnalysisInProgress || isSelectAllLoading || tableItems.length === 0}
                className={styles.selectAllCheckbox}
              />
            </TableCell>
            {visibleTableHeaders.map((header) => (
              <TableCell
                key={header.id}
                head
                className={clsx(
                  styles.tableCellHead,
                  header.id === 'inputProduct' && styles.inputProductHeadCell,
                  header.id === 'code' && styles.codeHeadCell,
                  header.id === 'description' && styles.descriptionHeadCell,
                  header.id === 'category' && styles.categoryHeadCell,
                  header.id === 'suppliers' && styles.suppliersHeadCell,
                  header.id === 'naceSector' && styles.naceSectorHeadCell,
                  header.id === 'additionalInformation' && styles.additionalInfoHeadCell,
                  header.id === 'source' && styles.sourceHeadCell,
                )}
              >
                {header.title}
              </TableCell>
            ))}
            <TableCell head className={clsx(styles.tableCellHead, styles.actionHeadCell)}>
              <span aria-hidden="true" />
            </TableCell>
          </TableHead>
        </Table>
      </div>

      <div
        id="bom-units-table-scroll"
        ref={tableScrollContainerRef}
        className={styles.tableScrollContainer}
      >
        {isAnalysisInProgress ? (
          <Table className={styles.table}>
            {renderTableColGroup()}
            <TableBody className={styles.tableBody}>
              <tr>
                <TableCell colSpan={visibleColumnsCount} className={styles.emptyStateCell}>
                  <BomUnitsAnalysisInProgress />
                </TableCell>
              </tr>
            </TableBody>
          </Table>
        ) : (
          <InfiniteScroll
            className={styles.tableScroll}
            dataLength={tableItems.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={false}
            scrollableTarget="bom-units-table-scroll"
            style={{ overflow: 'visible' }}
          >
            <Table className={styles.table}>
              {renderTableColGroup()}
              <TableBody className={styles.tableBody}>
                {tableItems.length ? tableItems.map((item, rowIndex) => (
                  <RowAICreatorBoM
                    key={item.id}
                    item={item}
                    rowIndex={rowIndex}
                    naceOptions={naceOptions}
                    categoryOptions={categoryOptions}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    focusCell={focusCell}
                    onStartEdit={onStartEdit}
                  />
                )) : (
                  <tr>
                    <TableCell colSpan={visibleColumnsCount} className={styles.emptyStateCell}>
                      <div className={styles.emptyState}>
                        <InfoIcon className={styles.emptyStateIcon} />
                        <p className={styles.emptyStateText}>
                          {emptyStateMessage}
                        </p>
                      </div>
                    </TableCell>
                  </tr>
                )}
              </TableBody>
            </Table>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}

export default BomUnitsTable;
